import { FieldSpecs, money, quoted } from '@api/ActivityLogs/DescribeChanges';
import { BranchOffice } from '@api/BranchOffices/BranchOffice';
import { DiscountType, PromotionalCode } from '@api/PromotionalCodes/PromotionalCode';

/**
 * Qué se audita de la CONFIGURACIÓN (M5 del `PLAN-BITACORA-ACTIVITY-LOGS.md`):
 * sucursales, códigos promocionales y usuarios del staff.
 *
 * Es el módulo del *"¿desde cuándo estamos cobrando eso?"*. Tres cosas que
 * cambian lo que se le cobra a un cliente —o quién puede entrar al sistema— y no
 * dejaban rastro hasta el 2026-09-11:
 *
 * 1. **Las tarifas de envío y el envío gratis**, que se editan desde DOS lugares
 *    (el atajo del sidebar y la pantalla de Sucursales) — así que el cambio podía
 *    venir de cualquiera de los dos y de nadie en particular.
 * 2. **Los horarios**, que deciden si la sucursal aparece abierta.
 * 3. **Los códigos promocionales**: un descuento directo sobre la venta.
 *
 * ⚠️ **Prender y apagar Pickup/Delivery también queda registrado**, y es el
 * evento más frecuente de los tres: es el control del día a día (cerrar Delivery
 * cuando la cocina se satura). Se describe como un ACTO — *"cerró el delivery"*—,
 * no como un cambio de `true` a `false`.
 */

export const BRANCH_OFFICE_FIELDS: FieldSpecs<BranchOffice> = {
    name: { label: 'el nombre', format: quoted },
    number: { label: 'el número de sucursal', format: quoted },
    status: { label: 'el estado', verbs: { on: 'activó la sucursal', off: 'desactivó la sucursal' } },
    address: { label: 'la dirección', format: quoted },
    phone: { label: 'el teléfono', format: quoted },
    hasPickup: { label: 'el pickup', verbs: { on: 'abrió el pickup', off: 'cerró el pickup' } },
    hasDelivery: { label: 'el delivery', verbs: { on: 'abrió el delivery', off: 'cerró el delivery' } },
    deactivationReason: { label: 'el motivo', format: quoted },
    // Lo que cambia lo que paga el cliente. Van como `mention` porque son objetos
    // anidados: el detalle de cuánto se dice aparte, con `DELIVERY_FIELDS`.
    delivery: { label: 'las tarifas de envío', mention: 'cambió las tarifas de envío' },
    schedule: { label: 'los horarios', mention: 'cambió los horarios de la sucursal' },
    manager: { label: 'el responsable', mention: 'cambió los datos del responsable' },
    notifyManagerOnNewOrder: {
        label: 'el aviso al responsable',
        verbs: { on: 'activó el aviso de pedidos al responsable', off: 'apagó el aviso de pedidos al responsable' }
    },
    inventoryDiscountEnabled: {
        label: 'el descuento de inventario',
        verbs: { on: 'ENCENDIÓ el descuento de inventario', off: 'apagó el descuento de inventario' }
    },
    location: { label: 'la ubicación', mention: 'cambió la ubicación de la sucursal' },
    googlePlaceId: { label: 'el lugar de Google', mention: 'cambió el lugar de Google de la sucursal' },
    distributionCenterId: { label: 'el CEDIS', mention: 'cambió el CEDIS que la surte' }
    // ⚠️ `unavailableInputs` (el "86") NO va en esta lista A PROPÓSITO: ya tiene su
    // propio evento desde antes (`setInputUnavailability`, que escribe con
    // arrayUnion/arrayRemove). Describirlo aquí registraría el mismo hecho dos
    // veces (D6). Un campo sin spec no se describe: ésa es la forma de excluirlo.
};

/** El detalle del envío, que es donde está el dinero. */
export const DELIVERY_FIELDS: FieldSpecs<Record<string, unknown>> = {
    initialFee: { label: 'la tarifa inicial de envío', format: money },
    costPerKm: { label: 'el costo por km', format: money },
    maxDistance: { label: 'la distancia máxima', format: (value) => `${Number(value) || 0} km` },
    freeDeliveryEnabled: {
        label: 'el envío gratis',
        verbs: { on: 'activó el envío gratis', off: 'desactivó el envío gratis' }
    },
    minAmountForFreeDelivery: { label: 'el mínimo para envío gratis', format: money }
};

export const PROMOTIONAL_CODE_FIELDS: FieldSpecs<PromotionalCode> = {
    code: { label: 'el código', format: quoted },
    // El descuento son DOS campos (tipo y valor) y de ellos sale lo que se deja
    // de cobrar: van los dos, para que la frase diga si pasó de % a monto fijo.
    discountValue: { label: 'el descuento' },
    discountType: { label: 'el tipo de descuento' },
    status: { label: 'el estado', verbs: { on: 'activó el código', off: 'desactivó el código' } },
    noLimit: { label: 'el uso ilimitado', verbs: { on: 'lo dejó sin límite de usos', off: 'le puso límite de usos' } },
    usageLimit: { label: 'el límite de usos' },
    branchOfficesAvailable: { label: 'las sucursales', mention: 'cambió en qué sucursales aplica el código' }
};

/**
 * El renglón de un código promocional BORRADO: lleva el código y el descuento
 * dentro del texto, porque el documento se va. Mismo criterio que el movimiento
 * de caja borrado.
 */
export const describeDeletedPromotionalCode = (
    promotionalCode: Pick<PromotionalCode, 'code' | 'discountType' | 'discountValue'>
): string => {
    const descuento =
        promotionalCode.discountType === DiscountType.Percent
            ? `${promotionalCode.discountValue}% de descuento`
            : `${money(promotionalCode.discountValue)} de descuento`;
    return `borró el código promocional "${promotionalCode.code}" (${descuento})`;
};

/**
 * El estado de un usuario del staff: es CONTROL DE ACCESO, así que se dice quién
 * quedó dentro o fuera, no un `true → false`.
 */
export const describeUserEmailChange = (name: string, previousEmail: string | undefined, email: string): string =>
    `cambió el correo con el que entra ${name}: de ${previousEmail || '(sin correo)'} a ${email}`;

/** Restablecer un PIN es control de acceso: queda A QUIÉN, nunca el PIN. */
export const describeUserPinReset = (name: string): string => `restableció el PIN de ${name}`;

export const describeUserStatus = (name: string, active: boolean): string =>
    active ? `reactivó el acceso de ${name}` : `dio de baja el acceso de ${name}`;
